import { BarChart as BarChartIcon } from 'lucide-react';
import type { CategoryBreakdown } from '../../api';

interface CategoryChartProps {
  byCategory: CategoryBreakdown[];
  isLoading?: boolean;
  /** Map of categoryId -> label from API (required for display) */
  categoryLabels?: Record<string, string>;
}

const CHART_COLORS = [
  '#DE1D3B',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#ec4899',
  '#f97316',
  '#06b6d4',
  '#6b7280',
];

function getColorForCategory(categoryId: string, index: number): string {
  const hash = categoryId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return CHART_COLORS[Math.abs(hash) % CHART_COLORS.length] ?? CHART_COLORS[index % CHART_COLORS.length] ?? '#6b7280';
}

export function CategoryChart({ byCategory, isLoading, categoryLabels }: CategoryChartProps) {
  const getLabel = (categoryId: string) =>
    categoryLabels?.[categoryId] ?? categoryId;
  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="h-4 bg-gray-200 rounded w-40 mb-1 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-28 mb-6 animate-pulse" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-3 bg-gray-200 rounded w-24 animate-pulse" />
              <div className="flex-1 h-6 bg-gray-100 rounded-full animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-8 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (byCategory.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Complaints by Category</h3>
        <p className="text-xs text-gray-500 mb-4">Distribution across complaint types</p>
        <div className="h-64 flex flex-col items-center justify-center text-gray-400">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <BarChartIcon className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm font-medium">No category data available</p>
          <p className="text-xs mt-1">Data will appear as complaints are submitted</p>
        </div>
      </div>
    );
  }

  // Sort by count descending and get the max for scaling
  const sortedData = [...byCategory].sort((a, b) => b.count - a.count);
  const maxCount = sortedData[0]?.count || 1;
  const total = sortedData.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Complaints by Category</h3>
          <p className="text-xs text-gray-500">Distribution across complaint types</p>
        </div>
        <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
          {sortedData.length} categories
        </span>
      </div>

      {/* Custom horizontal bar chart */}
      <div className="space-y-3">
        {sortedData.map((item, index) => {
          const percentage = Math.round((item.count / total) * 100);
          const barWidth = (item.count / maxCount) * 100;
          const color = getColorForCategory(item.category, index);

          return (
            <div key={item.category} className="group">
              <div className="flex items-center gap-3">
                {/* Label */}
                <div className="w-28 flex-shrink-0">
                  <span className="text-xs font-medium text-gray-700 truncate block">
                    {getLabel(item.category)}
                  </span>
                </div>

                {/* Bar container */}
                <div className="flex-1 h-7 bg-gray-50 rounded-full overflow-hidden relative">
                  {/* Bar */}
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out group-hover:opacity-90"
                    style={{
                      width: `${Math.max(barWidth, 8)}%`,
                      backgroundColor: color,
                      opacity: 0.85,
                    }}
                  />
                  {/* Value badge inside bar */}
                  {barWidth > 25 && (
                    <span
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-white"
                      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                    >
                      {item.count}
                    </span>
                  )}
                </div>

                {/* Count and percentage */}
                <div className="w-16 flex-shrink-0 text-right">
                  {barWidth <= 25 && (
                    <span className="text-sm font-semibold text-gray-900 mr-1">{item.count}</span>
                  )}
                  <span className="text-xs text-gray-400">({percentage}%)</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-500">Total complaints</span>
        <span className="text-sm font-semibold text-gray-900">{total}</span>
      </div>
    </div>
  );
}
